<template>
  <DrawerModal
    :is-open="isOpen"
    title="Availability"
    :has-changes="hasChanges"
    :is-loading="isSubmitting"
    :is-disabled="isInitializing || !!loadError"
    @close="closeWithoutSaving"
    @submit="handleChooseAvailability"
  >
    <div class="time-selection">
      <Text v-if="isInitializing" class="availability-adjustment">Loading availability horizon...</Text>

      <Text v-else-if="loadError" class="availability-adjustment">{{ loadError }}</Text>

      <template v-else>
        <div
          v-for="(dayAvailability, dayIndex) in dayAvailabilities"
          :key="dayAvailability.intentId"
          :class="['time-selection-day', { 'is-disabled': !dayAvailability.isEnabled }]"
        >
          <div class="day-header">
            <div class="day-label">{{ toDisplayLabel(dayAvailability) }}</div>

            <GamSwitch
              class="day-switch"
              :name="`availability-${dayAvailability.date}`"
              :type="GamSwitchValueType.SWITCH"
              :position="GamSwitchPosition.RIGHT"
              :checked="dayAvailability.isEnabled"
              :disabled="isSubmitting"
              @update:checked="(value) => handleDayEnabledToggle(dayIndex, value)"
            />
          </div>

          <div class="time-span">
            <div class="start-end">
              <span class="time-label">{{ toTimeInputValue(dayAvailability.startHour) }}</span>
              <span class="time-separator">-</span>
              <span class="time-label">{{ toTimeInputValue(dayAvailability.endHour) }}</span>
            </div>

            <div class="time-slider">
              <div class="slider-track-wrapper">
                <GamSlider
                  class="time-slider-control"
                  :min="{ value: dayAvailability.minStartTime }"
                  :max="{ value: dayAvailability.maxEndTime }"
                  :value="[dayAvailability.startHour, dayAvailability.endHour]"
                  :step="0.5"
                  :disabled="!dayAvailability.isEnabled || isSubmitting"
                  @change="(lower, higher) => handleDaySpanChange(dayIndex, lower, higher)"
                />

                <div v-if="dayAvailability.gambitWindows.length" class="gambit-windows-track">
                  <div
                    v-for="(gw, gwIndex) in dayAvailability.gambitWindows"
                    :key="gwIndex"
                    class="gambit-window-overlay"
                    :style="gambitWindowStyle(gw, dayAvailability)"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <Text v-if="submitError" class="availability-adjustment">{{ submitError }}</Text>
    </div>
  </DrawerModal>
</template>

<script setup lang="ts">
import type { ConfirmedGambitWindow, SparkIntent } from '@/core/data/spark/spark.interface';
import { useSparkStore } from '@/stores/spark.store';
import DrawerModal from '@/views/components/DrawerModal.vue';
import GamSlider from '@/views/components/GamSlider.vue';
import GamSwitch from '@/views/components/GamSwitch.vue';
import { GamSwitchPosition, GamSwitchValueType } from '@/views/composables/constants/components/gamSwitch.constants';
import Text from '@/views/main/sparks/components/Text.vue';
import { addDays, format, isSameDay, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useToast } from 'vue-toastification';

interface ChooseAvailabilityProps {
  isOpen: boolean;
  onConfirm?: (savedDates: string[]) => void | Promise<void>;
}

const props = defineProps<ChooseAvailabilityProps>();
const emit = defineEmits<{ close: [] }>();
const toast = useToast();

const sparkStore = useSparkStore();
const { intents, isLoadingIntents } = storeToRefs(sparkStore);

const MIN_START_TIME = 8;
const MAX_END_TIME = 23;

interface GambitWindowHours {
  startHour: number;
  endHour: number;
}

interface DayAvailability {
  date: string;
  label: string;
  intentId: string;
  isEnabled: boolean;
  startHour: number;
  endHour: number;
  minStartTime: number;
  maxEndTime: number;
  cityName: string;
  gambitWindows: GambitWindowHours[];
  initial: {
    isEnabled: boolean;
    startHour: number;
    endHour: number;
    minStartTime: number;
    maxEndTime: number;
  };
}

const minStartTime = MIN_START_TIME;
const maxEndTime = MAX_END_TIME;
const dayAvailabilities = ref<DayAvailability[]>([]);
const isInitializing = ref(true);
const isSubmitting = ref(false);
const loadError = ref<string | null>(null);
const submitError = ref<string | null>(null);

const initialHorizonCityId = ref<string | null>(null);
const selectedCityId = ref<string | null>(null);

const isCityDirty = computed(() => {
  if (!initialHorizonCityId.value || !selectedCityId.value) {
    return false;
  }

  return selectedCityId.value !== initialHorizonCityId.value;
});

const hasChanges = computed(() => {
  return isCityDirty.value || dayAvailabilities.value.some((d) => isRowDirty(d));
});

const closeWithoutSaving = (): void => {
  emit('close');
};

const closeAfterSuccessfulSave = async (savedDates: string[]): Promise<void> => {
  if (props.onConfirm) {
    await props.onConfirm(savedDates);
    return;
  }

  emit('close');
};

const clampStartHour = (hour: number): number => {
  return Math.min(Math.max(hour, minStartTime), maxEndTime - 1);
};

const clampEndHour = (hour: number): number => {
  return Math.max(Math.min(hour, maxEndTime), minStartTime + 1);
};

const enforceHourOrder = (dayAvailability: DayAvailability): void => {
  if (dayAvailability.startHour >= dayAvailability.endHour) {
    dayAvailability.endHour = Math.min(maxEndTime, dayAvailability.startHour + 1);
  }

  if (dayAvailability.endHour <= dayAvailability.startHour) {
    dayAvailability.startHour = Math.max(minStartTime, dayAvailability.endHour - 1);
  }
};

const updateDayAvailability = (dayIndex: number, update: (dayAvailability: DayAvailability) => void): void => {
  const dayAvailability = dayAvailabilities.value[dayIndex];

  if (!dayAvailability) {
    return;
  }

  update(dayAvailability);
  enforceHourOrder(dayAvailability);
};

const toTimeInputValue = (hour: number): string => {
  if (hour % 1 === 0.5) {
    return `${(hour - 0.5).toString().padStart(2, '0')}:30`;
  }

  return `${hour.toString().padStart(2, '0')}:00`;
};

const toHourValue = (hour: number): string => {
  if (hour % 1 === 0.5) {
    return `${(hour - 0.5).toString().padStart(2, '0')}:30`;
  }

  return `${hour.toString().padStart(2, '0')}:00`;
};

const isRowDirty = (dayAvailability: DayAvailability): boolean => {
  return (
    dayAvailability.isEnabled !== dayAvailability.initial.isEnabled ||
    dayAvailability.startHour !== dayAvailability.initial.startHour ||
    dayAvailability.endHour !== dayAvailability.initial.endHour
  );
};

const getIntentHourRange = (
  intent: SparkIntent,
): { startHour: number; endHour: number; minStartTime: number; maxEndTime: number } => {
  const tz = intent.timeZone || 'UTC';
  const startH = Number.parseInt(formatInTimeZone(intent.timeSpan.start, tz, 'H'), 10);
  const startM = Number.parseInt(formatInTimeZone(intent.timeSpan.start, tz, 'm'), 10);
  const endH = Number.parseInt(formatInTimeZone(intent.timeSpan.end, tz, 'H'), 10);
  const endM = Number.parseInt(formatInTimeZone(intent.timeSpan.end, tz, 'm'), 10);

  const startHour = clampStartHour(startH + (startM >= 30 ? 0.5 : 0));
  const endHour = clampEndHour(endH + (endM >= 30 ? 0.5 : 0));

  if (endHour <= startHour) {
    return {
      startHour,
      endHour: Math.min(maxEndTime, startHour + 1),
      minStartTime: intent.minStartTime,
      maxEndTime: intent.maxEndTime - 1,
    };
  }

  return { startHour, endHour, minStartTime: intent.minStartTime, maxEndTime: intent.maxEndTime };
};

const toGambitWindowHours = (windows: ConfirmedGambitWindow[] | undefined, timeZone: string): GambitWindowHours[] => {
  if (!windows?.length) {
    return [];
  }

  return windows.map((w) => {
    const sH = Number.parseInt(formatInTimeZone(w.start, timeZone, 'H'), 10);
    const sM = Number.parseInt(formatInTimeZone(w.start, timeZone, 'm'), 10);
    const eH = Number.parseInt(formatInTimeZone(w.end, timeZone, 'H'), 10);
    const eM = Number.parseInt(formatInTimeZone(w.end, timeZone, 'm'), 10);

    return {
      startHour: sH + (sM >= 30 ? 0.5 : 0),
      endHour: eH + (eM >= 30 ? 0.5 : 0),
    };
  });
};

const mapIntentToRow = (intent: SparkIntent): DayAvailability => {
  const { startHour, endHour, minStartTime, maxEndTime } = getIntentHourRange(intent);
  const parsedDate = parseISO(intent.date);
  const isEnabled = !intent.isDisabled;
  const tz = intent.timeZone || 'UTC';

  return {
    date: intent.date,
    label: `${format(parsedDate, 'EEEE')} · ${format(parsedDate, 'MMM d')}`,
    intentId: intent.id,
    isEnabled,
    startHour,
    endHour,
    minStartTime,
    maxEndTime,
    cityName: intent.city?.name || '',
    gambitWindows: toGambitWindowHours(intent.confirmedGambitWindows, tz),
    initial: {
      isEnabled,
      startHour,
      endHour,
      minStartTime,
      maxEndTime,
    },
  };
};

const toDisplayLabel = (dayAvailability: DayAvailability): string => {
  const parsedDate = parseISO(dayAvailability.date);
  const now = new Date();

  if (isSameDay(parsedDate, now)) {
    return 'Today';
  }

  if (isSameDay(parsedDate, addDays(now, 1))) {
    return 'Tomorrow';
  }

  return dayAvailability.label;
};

const gambitWindowStyle = (gw: GambitWindowHours, day: DayAvailability): Record<string, string> => {
  const range = day.maxEndTime - day.minStartTime;

  if (range <= 0) {
    return { display: 'none' };
  }

  const clampedStart = Math.max(gw.startHour, day.minStartTime);
  const clampedEnd = Math.min(gw.endHour, day.maxEndTime);

  if (clampedEnd <= clampedStart) {
    return { display: 'none' };
  }

  const leftPct = ((clampedStart - day.minStartTime) / range) * 100;
  const widthPct = ((clampedEnd - clampedStart) / range) * 100;

  return {
    left: `${leftPct}%`,
    width: `${widthPct}%`,
  };
};

const buildAvailabilityRows = (sourceIntents: SparkIntent[]): DayAvailability[] => {
  const sorted = [...sourceIntents].sort((a, b) => a.date.localeCompare(b.date));
  const rows = sorted.slice(0, 7).map(mapIntentToRow);

  for (const row of rows) {
    enforceHourOrder(row);
    row.initial = {
      isEnabled: row.isEnabled,
      startHour: row.startHour,
      endHour: row.endHour,
      minStartTime: row.minStartTime,
      maxEndTime: row.maxEndTime,
    };
  }

  return rows;
};

const waitForIntentsLoad = async (): Promise<void> => {
  if (!isLoadingIntents.value) {
    return;
  }

  await new Promise<void>((resolve) => {
    const stop = watch(isLoadingIntents, (loading) => {
      if (!loading) {
        stop();
        resolve();
      }
    });
  });
};

const initializeForm = async (): Promise<void> => {
  isInitializing.value = true;
  isSubmitting.value = false;
  loadError.value = null;
  submitError.value = null;

  try {
    if (!intents.value.length) {
      if (isLoadingIntents.value) {
        await waitForIntentsLoad();
      } else {
        await sparkStore.fetchIntents();
      }
    }

    if (!intents.value.length) {
      throw new Error('No availability horizon found.');
    }

    const rows = buildAvailabilityRows(intents.value);

    if (!rows.length) {
      throw new Error('No availability horizon found.');
    }

    dayAvailabilities.value = rows;
    const horizonIntent = [...intents.value].sort((a, b) => a.date.localeCompare(b.date)).slice(0, 7)[0];
    initialHorizonCityId.value = horizonIntent?.cityId || null;
    selectedCityId.value = horizonIntent?.cityId || null;
  } catch (error) {
    loadError.value = error instanceof Error ? error.message : 'Could not load availability horizon.';
  } finally {
    isInitializing.value = false;
  }
};

const handleDaySpanChange = (dayIndex: number, lower: number, higher?: number): void => {
  if (!Number.isFinite(lower) || !Number.isFinite(higher)) {
    return;
  }

  if (!dayAvailabilities.value[dayIndex]?.isEnabled) {
    return;
  }

  updateDayAvailability(dayIndex, (dayAvailability) => {
    dayAvailability.startHour = clampStartHour(lower);
    dayAvailability.endHour = clampEndHour(higher || MAX_END_TIME);
  });
};

const handleDayEnabledToggle = (dayIndex: number, isEnabled: boolean): void => {
  updateDayAvailability(dayIndex, (dayAvailability) => {
    dayAvailability.isEnabled = isEnabled;
  });
};

const handleChooseAvailability = async () => {
  if (isInitializing.value || isSubmitting.value || loadError.value) {
    return;
  }

  submitError.value = null;

  const updates = dayAvailabilities.value
    .filter((dayAvailability) => isRowDirty(dayAvailability))
    .map((dayAvailability) => ({
      date: dayAvailability.date,
      isEnabled: dayAvailability.isEnabled,
      start: toHourValue(dayAvailability.startHour),
      end: toHourValue(dayAvailability.endHour),
    }));

  const allDays = dayAvailabilities.value.map((dayAvailability) => ({
    date: dayAvailability.date,
    isEnabled: dayAvailability.isEnabled,
    start: toHourValue(dayAvailability.startHour),
    end: toHourValue(dayAvailability.endHour),
  }));

  const shouldUpdateHorizonCity = isCityDirty.value;

  if (!updates.length && !shouldUpdateHorizonCity) {
    closeWithoutSaving();
    return;
  }

  isSubmitting.value = true;

  if (shouldUpdateHorizonCity) {
    if (!selectedCityId.value) {
      isSubmitting.value = false;
      submitError.value = 'Choose a city before saving.';
      toast.error(submitError.value);
      return;
    }

    const result = await sparkStore.saveAvailabilityHorizon({
      cityId: selectedCityId.value,
      days: allDays,
    });

    if (!result.ok) {
      isSubmitting.value = false;
      submitError.value = result.error || 'Failed to save availability horizon.';
      toast.error(submitError.value);
      return;
    }

    await sparkStore.fetchIntents({ refreshExisting: true });
  } else {
    const result = await sparkStore.saveAvailabilityDays(updates);

    if (!result.ok) {
      isSubmitting.value = false;
      submitError.value = `Failed to save: ${result.failedDates.join(', ')}`;
      return;
    }
  }

  const savedDates = shouldUpdateHorizonCity ? allDays.map((day) => day.date) : updates.map((day) => day.date);
  await closeAfterSuccessfulSave(savedDates);
};

watch(
  () => props.isOpen,
  (open) => {
    if (open) {
      void initializeForm();
    }
  },
  { immediate: true },
);
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
.time-selection {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.25rem;
  width: 100%;
  background: rgba(255, 255, 255, 0.04);
  border-radius: var(--radius-small);
  padding: var(--spacing-md) var(--spacing-l);
}

.time-selection-day {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--color-white-10);
}

.time-selection-day.is-disabled .time-span {
  opacity: 0.45;
}

.time-selection-day:last-child {
  padding-bottom: 0;
  border-bottom: none;
}

.day-header {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.day-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--color-white-60);
}

.day-switch {
  width: auto !important;
  flex-shrink: 0;
}

.time-span {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: stretch;
  gap: 1rem;
}

.start-end {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-size: 14px;
  letter-spacing: 0.075em;
}

.time-label {
  text-align: center;
  user-select: none;
  font-variant-numeric: tabular-nums;
}

.time-separator {
  user-select: none;
}

.time-slider {
  margin-bottom: 0.5rem;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.slider-track-wrapper {
  position: relative;
  width: 100%;
}

.gambit-windows-track {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 10px;
  right: 10px;
  pointer-events: none;
  z-index: 0;
}

.gambit-window-overlay {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  height: 8px;
  border-radius: 4px;
  background: var(--color-white);
  pointer-events: none;
}
@include hover-capable {
  .availability-adjustment :deep(a, a:visited) {
    text-decoration: none;
    color: var(--color-pink);
  }
}
</style>
